import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import ReactECharts from 'echarts-for-react';
import 'echarts-wordcloud';
import {
  WidgetDataResult,
  WidgetChartConfig,
  WidgetClickPoint,
  WordCloudMode,
} from '../types';
import {
  mergeEChartsOption,
  formatNumber,
  escapeHtml,
  ECHARTS_TOOLTIP_STYLE,
} from './echartsUtils';
import {
  buildWordCloudItems,
  buildWordCloudSeries,
  filterWordCloudItems,
  formatTrend,
  getWordColors,
  hiddenKey,
  readSampleSize,
  resolveColorBy,
  availableColorModes,
  SENTIMENT_WORD_COLORS,
  WordCloudColorBy,
  TREND_COLORS,
  WORD_CLOUD_DEFAULTS,
  WORD_CLOUD_COLOR,
  WORD_DRAW_SCALE,
  WordCloudItem,
} from './wordCloudUtils';
import { SENTIMENT_GLYPH, TREND_GLYPHS, WordCloudList } from './WordCloudList';
import styles from './ChartWordCloud.module.scss';

interface ChartWordCloudProps {
  data: WidgetDataResult;
  config: WidgetChartConfig;
  onPointClick?: (point: WidgetClickPoint) => void;
  /** Keys the viewer's hidden words in localStorage. Without it they last until reload. */
  widgetId?: string;
  /** Edit mode only: moves the viewer's hidden words into the widget's stop-word list. */
  onSaveStopWords?: (words: string[]) => void;
}

type WordCloudView = 'cloud' | 'list';

// Stable reference: a fresh array per render would re-run the cloud layout.
const DEFAULT_COLORS = [WORD_CLOUD_COLOR];

const MODES: WordCloudMode[] = ['keywords', 'emojis', 'both'];
const VIEWS: WordCloudView[] = ['cloud', 'list'];
// Matches the app font in App.scss; only used until the real one is read from the DOM.
const FALLBACK_FONT = "Inter, 'Lexend Deca', sans-serif";
// Re-layout only when the card changes by a visible amount, not on every pixel of a drag.
const SIZE_STEP = 16;
const HIDDEN_STORAGE_PREFIX = 'customDashboard:wordCloudHidden:';

interface ChartBox {
  width: number;
  height: number;
  fontFamily: string;
}

/**
 * Shrinks freshly drawn words inside the box they were laid out in (see
 * WORD_DRAW_SCALE). Runs on every ECharts `rendered` event; each word is touched once.
 */
function padDrawnWords(_: unknown, chart: any) {
  const series = chart?.getModel?.()?.getSeriesByIndex?.(0);
  series?.getData?.()?.eachItemGraphicEl?.((el: any) => {
    if (!el || el.__wordCloudPadded) return;
    el.__wordCloudPadded = true;
    el.scaleX *= WORD_DRAW_SCALE;
    el.scaleY *= WORD_DRAW_SCALE;
    el.markRedraw();
  });
}

/** Words this viewer dismissed, kept per widget in localStorage when it is available. */
function useHiddenWords(widgetId: string | undefined) {
  const storageKey = widgetId ? `${HIDDEN_STORAGE_PREFIX}${widgetId}` : null;
  const [hidden, setHidden] = useState<string[]>(() => {
    if (!storageKey) return [];
    try {
      const parsed = JSON.parse(localStorage.getItem(storageKey) ?? '[]');
      return Array.isArray(parsed)
        ? parsed.filter((w) => typeof w === 'string')
        : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!storageKey) return;
    try {
      if (hidden.length) {
        localStorage.setItem(storageKey, JSON.stringify(hidden));
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch {
      // Private mode or blocked storage: the list simply lasts until reload.
    }
  }, [storageKey, hidden]);

  const hide = useCallback((name: string) => {
    setHidden((prev) =>
      prev.some((w) => hiddenKey(w) === hiddenKey(name))
        ? prev
        : [...prev, name],
    );
  }, []);
  const restore = useCallback((name: string) => {
    setHidden((prev) => prev.filter((w) => hiddenKey(w) !== hiddenKey(name)));
  }, []);
  const clear = useCallback(() => setHidden([]), []);

  return { hidden, hide, restore, clear };
}

export const ChartWordCloud: React.FC<ChartWordCloudProps> = ({
  data,
  config,
  onPointClick,
  widgetId,
  onSaveStopWords,
}) => {
  const { t } = useTranslation();
  const wc = config.wordCloudConfig;
  const allowHide = wc?.allowHide !== false;

  const configuredMode = wc?.mode ?? WORD_CLOUD_DEFAULTS.mode;
  const [mode, setMode] = useState<WordCloudMode>(configuredMode);
  // Admin changed the default in the editor — follow it.
  useEffect(() => setMode(configuredMode), [configuredMode]);

  const configuredView: WordCloudView = wc?.view === 'list' ? 'list' : 'cloud';
  const [view, setView] = useState<WordCloudView>(configuredView);
  useEffect(() => setView(configuredView), [configuredView]);

  const { hidden, hide, restore, clear } = useHiddenWords(widgetId);
  const hiddenKeys = useMemo(() => new Set(hidden.map(hiddenKey)), [hidden]);

  const allItems = useMemo(
    () => buildWordCloudItems(data, config.columnMapping, wc),
    [data, config.columnMapping, wc],
  );
  const hasKeywords = allItems.some((i) => i.kind === 'keyword');
  const hasEmojis = allItems.some((i) => i.kind === 'emoji');
  const showModeToggle =
    wc?.showModeToggle !== false && hasKeywords && hasEmojis;
  const showViewToggle = wc?.showViewToggle !== false;

  const items = useMemo(
    () =>
      filterWordCloudItems(
        allItems,
        mode,
        wc,
        allowHide ? hiddenKeys : undefined,
      ),
    [allItems, mode, wc, allowHide, hiddenKeys],
  );
  const hasContent = items.length > 0;
  const showCloud = hasContent && view === 'cloud';

  // Colour can show frequency, trend or sentiment, whichever the data carries.
  const colorModes = useMemo(() => availableColorModes(items), [items]);
  const [chosenColor, setChosenColor] = useState<WordCloudColorBy | null>(null);
  // Admin changed the default in the editor — drop the viewer's override.
  useEffect(() => setChosenColor(null), [wc?.colorBy]);
  const colorBy = useMemo(
    () => resolveColorBy(items, wc, chosenColor),
    [items, wc, chosenColor],
  );
  const showColorToggle =
    wc?.showColorToggle !== false && colorModes.length > 1;
  const hasTrend = colorModes.includes('trend');
  const hasSentiment = colorModes.includes('sentiment');
  const colors = config.colors?.length ? config.colors : DEFAULT_COLORS;
  const itemColors = useMemo(
    () => getWordColors(items, colors, colorBy),
    [items, colors, colorBy],
  );
  const trendLabel = wc?.trendLabel || t('vs last week');
  const sentimentLabels = useMemo(
    () => ({
      positive: t('Positive'),
      neutral: t('Neutral'),
      negative: t('Negative'),
    }),
    [t],
  );

  // The cloud layout needs the card's pixel size and its real font family up front.
  const chartRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<ChartBox | null>(null);
  useEffect(() => {
    const el = chartRef.current;
    if (!el) return undefined;
    const measure = () => {
      const width = Math.round(el.clientWidth / SIZE_STEP) * SIZE_STEP;
      const height = Math.round(el.clientHeight / SIZE_STEP) * SIZE_STEP;
      if (!width || !height) return;
      const fontFamily = getComputedStyle(el).fontFamily || FALLBACK_FONT;
      setBox((prev) =>
        prev &&
        prev.width === width &&
        prev.height === height &&
        prev.fontFamily === fontFamily
          ? prev
          : { width, height, fontFamily },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
    // The chart node unmounts in list view or when nothing is left to show.
  }, [showCloud]);

  const option = useMemo(() => {
    if (!box) return null;
    const base = {
      tooltip: {
        trigger: 'item' as const,
        ...ECHARTS_TOOLTIP_STYLE,
        formatter: (params: any) => {
          const raw = params.data?.raw ?? params.value;
          const val =
            typeof raw === 'number' ? formatNumber(raw) : escapeHtml(raw);
          const lines = [`${escapeHtml(params.name)}: <b>${val}</b>`];
          const sentiment = params.data?.sentiment;
          if (sentiment) {
            const label = sentiment.label as keyof typeof SENTIMENT_WORD_COLORS;
            lines.push(
              `<span style="color:${
                SENTIMENT_WORD_COLORS[label]
              }">${SENTIMENT_GLYPH}</span> ${escapeHtml(
                sentimentLabels[label],
              )}`,
            );
          }
          const trend = params.data?.trend;
          if (trend) {
            lines.push(
              `<span style="color:${
                TREND_COLORS[trend.direction as keyof typeof TREND_COLORS]
              }">${
                TREND_GLYPHS[trend.direction as keyof typeof TREND_GLYPHS]
              }</span> ${formatTrend(trend.change)} ${escapeHtml(trendLabel)}`,
            );
          }
          if (allowHide) {
            lines.push(
              `<span style="font-size:12px;opacity:0.65">${escapeHtml(
                t('Right-click to hide'),
              )}</span>`,
            );
          }
          return lines.join('<br/>');
        },
        appendToBody: true,
      },
      series: [
        buildWordCloudSeries(items, {
          ...box,
          colors,
          colorBy,
          config: wc,
        }),
      ],
    };
    return mergeEChartsOption(base, config.echartsOption);
  }, [
    box,
    items,
    colors,
    colorBy,
    wc,
    config.echartsOption,
    allowHide,
    trendLabel,
    sentimentLabels,
    t,
  ]);

  const selectItem = useMemo(() => {
    if (!onPointClick) return undefined;
    return (item: WordCloudItem) =>
      onPointClick({
        label: item.name,
        key: item.name,
        value: item.value,
        extra: { ...item.row } as Record<
          string,
          string | number | boolean | null
        >,
      });
  }, [onPointClick]);

  const onEvents = useMemo(() => {
    const events: Record<string, (params: any, chart: any) => void> = {
      rendered: padDrawnWords,
    };
    if (selectItem) {
      events.click = (params: any) => {
        const item = items[params.dataIndex];
        if (item) selectItem(item);
      };
    }
    if (allowHide) {
      events.contextmenu = (params: any) => {
        if (params?.dataIndex === undefined) return;
        // Keep the browser menu from covering the cloud the word just left.
        params.event?.event?.preventDefault?.();
        const item = items[params.dataIndex];
        if (item) hide(item.name);
      };
    }
    return events;
  }, [selectItem, items, allowHide, hide]);

  const sampleSize = readSampleSize(data, wc);
  const caption =
    wc?.caption ||
    (sampleSize
      ? t('Based on the latest {{n}} messages', {
          n: sampleSize.toLocaleString(),
        })
      : '');

  const colorLabels: Record<WordCloudColorBy, string> = {
    frequency: t('Frequency'),
    trend: t('Trend'),
    sentiment: t('Sentiment'),
  };
  const modeLabels: Record<WordCloudMode, string> = {
    keywords: t('Keywords'),
    emojis: t('Emojis'),
    both: t('Both'),
  };
  const viewLabels: Record<WordCloudView, string> = {
    cloud: t('Cloud'),
    list: t('List'),
  };

  const summary = items
    .slice(0, 10)
    .map((i) => `${i.name} (${formatNumber(i.value)})`)
    .join(', ');

  const saveStopWords = () => {
    if (!onSaveStopWords) return;
    onSaveStopWords(hidden);
    clear();
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.meta}>
          {colorBy === 'sentiment' && (
            <span className={styles.legend}>
              {(['positive', 'neutral', 'negative'] as const).map((label) => (
                <span key={label} className={styles.legendItem}>
                  <span
                    aria-hidden="true"
                    style={{ color: SENTIMENT_WORD_COLORS[label] }}
                  >
                    {SENTIMENT_GLYPH}
                  </span>{' '}
                  {sentimentLabels[label]}
                </span>
              ))}
            </span>
          )}
          {colorBy === 'trend' && (
            <span className={styles.legend}>
              <span className={styles.legendItem}>
                <span aria-hidden="true" style={{ color: TREND_COLORS.rising }}>
                  {TREND_GLYPHS.rising}
                </span>{' '}
                {t('Rising')}
              </span>
              <span className={styles.legendItem}>
                <span
                  aria-hidden="true"
                  style={{ color: TREND_COLORS.falling }}
                >
                  {TREND_GLYPHS.falling}
                </span>{' '}
                {t('Falling')}
              </span>
              <span className={styles.legendItem}>
                <span aria-hidden="true" style={{ color: TREND_COLORS.steady }}>
                  {TREND_GLYPHS.steady}
                </span>{' '}
                {t('Steady')}
              </span>
              <span className={styles.legendNote}>{trendLabel}</span>
            </span>
          )}
        </div>
        <div className={styles.controls} data-html2canvas-ignore="true">
          {showColorToggle && (
            <label className={styles.colorSelect}>
              <span>{t('Color')}</span>
              <select
                value={colorBy}
                onChange={(e) =>
                  setChosenColor(e.target.value as WordCloudColorBy)
                }
              >
                {colorModes.map((m) => (
                  <option key={m} value={m}>
                    {colorLabels[m]}
                  </option>
                ))}
              </select>
            </label>
          )}
          {showModeToggle && (
            <div
              className={styles.modeGroup}
              role="group"
              aria-label={t('Show')}
            >
              {MODES.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={styles.modeButton}
                  aria-pressed={mode === m}
                  onClick={() => setMode(m)}
                >
                  {modeLabels[m]}
                </button>
              ))}
            </div>
          )}
          {showViewToggle && (
            <div
              className={styles.modeGroup}
              role="group"
              aria-label={t('View')}
            >
              {VIEWS.map((v) => (
                <button
                  key={v}
                  type="button"
                  className={styles.modeButton}
                  aria-pressed={view === v}
                  onClick={() => setView(v)}
                >
                  {viewLabels[v]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {allowHide && hidden.length > 0 && (
        <div className={styles.hiddenBar} data-html2canvas-ignore="true">
          <span className={styles.hiddenLabel}>{t('Hidden')}:</span>
          <ul className={styles.hiddenList}>
            {hidden.map((word) => (
              <li key={word}>
                <button
                  type="button"
                  className={styles.chip}
                  aria-label={t('Show {{word}} again', { word })}
                  title={t('Show {{word}} again', { word })}
                  onClick={() => restore(word)}
                >
                  {word} <span aria-hidden="true">×</span>
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className={styles.linkButton} onClick={clear}>
            {t('Show all')}
          </button>
          {onSaveStopWords && (
            <button
              type="button"
              className={styles.linkButton}
              onClick={saveStopWords}
            >
              {t('Add to stop words')}
            </button>
          )}
        </div>
      )}

      {!hasContent && <p className={styles.empty}>{t('No data to display')}</p>}

      {showCloud && (
        <div
          ref={chartRef}
          className={styles.chart}
          role="img"
          aria-label={`${t('Word cloud')}: ${summary}`}
        >
          {/*
            Do NOT pass notMerge. The plugin lays words out on a timer and cancels the
            previous run only through the series model it is merged into. notMerge
            replaces that model, so two updates in quick succession (a resize right
            after mounting, for example) leave both layouts running and every word is
            drawn twice.
          */}
          {option && (
            <ReactECharts
              option={option}
              style={{ width: '100%', height: '100%' }}
              opts={{ renderer: 'svg' }}
              onEvents={onEvents}
            />
          )}
        </div>
      )}

      {hasContent && view === 'list' && (
        <WordCloudList
          items={items}
          colors={itemColors}
          showTrend={hasTrend}
          showSentiment={hasSentiment}
          sentimentLabels={sentimentLabels}
          trendLabel={trendLabel}
          onSelect={selectItem}
          onHide={allowHide ? (item) => hide(item.name) : undefined}
        />
      )}

      {caption && <p className={styles.caption}>{caption}</p>}
    </div>
  );
};
